/** Duration of toast enter/exit transitions in milliseconds. */
export const TOAST_ANIMATION_MS = 300;

/** Desktop toast offset: main header height (~120px) + 20px gap. */
export const DESKTOP_TOAST_TOP_CLASS = "md:top-140";

/** Desktop page-level toast bottom offset (reminders success toast, etc.). */
export const DESKTOP_TOAST_BOTTOM_CLASS = "bottom-80";

/** Mobile / page-level toast bottom offset (above sticky footers). */
export const TOAST_MOBILE_BOTTOM_CLASS = "bottom-120";

/** Drawer toast bottom offset (above drawer footer). */
export const TOAST_DRAWER_BOTTOM_CLASS = "bottom-[120px]";

/** Full-width toast with 20px horizontal inset (page-level mobile / non-drawer). */
export const TOAST_FULL_WIDTH_GAPS_CLASS = "inset-x-20";

/** Drawer toast anchored to drawer panel with 20px horizontal inset. */
export const TOAST_DRAWER_FULL_WIDTH_GAPS_CLASS = "absolute left-20 right-20";

/** Embedded pre-checkout drawer toast — fixed to viewport so width matches drawer panel. */
export const TOAST_EMBEDDED_PRECHECKOUT_POSITION = `fixed left-20 right-20 ${TOAST_DRAWER_BOTTOM_CLASS}`;

/** Desktop non-drawer toast width constraints. */
export const TOAST_DESKTOP_WIDTH_CLASS = "min-w-[360px] max-w-[420px]";

/** Toast stacking order — always above drawers, headers, and sticky footers. */
export const TOAST_Z_INDEX_CLASS = "z-[99999]";

/**
 * Ensures toast containers always use the global high z-index.
 *
 * @param {string} classes
 * @returns {string}
 */
export const applyToastZIndex = (classes) => {
  if (!classes) {
    return "";
  }

  const withoutZIndex = classes
    .replace(/\bz-\[[^\]]+\]\b/g, "")
    .replace(/\bz-\S+/g, "")
    .replace(/\s+/g, " ")
    .trim();

  return `${withoutZIndex} ${TOAST_Z_INDEX_CLASS}`.trim();
};

/**
 * Resolves toast slide direction from context and positioning classes.
 *
 * @param {Object} options
 * @param {boolean} [options.isDrawer]
 * @param {boolean|undefined} options.isMobile
 * @param {"top"|"bottom"|"auto"|undefined} options.placement
 * @param {string} [options.position]
 * @returns {"top"|"bottom"|"auto"}
 */
export const getToastPlacement = ({ isDrawer, isMobile, placement, position = "" }) => {
  if (placement === "top" || placement === "bottom") {
    return placement;
  }

  const positionLower = position.toLowerCase();
  if (positionLower.includes("bottom")) {
    return "bottom";
  }
  if (positionLower.includes("top")) {
    return "top";
  }

  if (isDrawer) {
    return "bottom";
  }
  if (isMobile === true) {
    return "bottom";
  }
  if (isMobile === false) {
    return "top";
  }

  return "auto";
};

/**
 * Returns transform/opacity classes for the current animation phase.
 *
 * @param {"enter"|"visible"|"exit"} phase
 * @param {"top"|"bottom"|"auto"} resolvedPlacement
 * @param {boolean|undefined} isMobile
 * @returns {string}
 */
export const getToastAnimationClasses = (phase, resolvedPlacement, isMobile) => {
  const transition = "transition-all duration-300 ease-out";
  const isHidden = phase === "enter" || phase === "exit";

  if (!isHidden) {
    return `${transition} translate-y-0 opacity-100`;
  }

  if (resolvedPlacement === "bottom") {
    return `${transition} translate-y-full opacity-0`;
  }

  if (resolvedPlacement === "top") {
    return `${transition} -translate-y-full opacity-0`;
  }

  if (isMobile === true) {
    return `${transition} translate-y-full opacity-0`;
  }

  if (isMobile === false) {
    return `${transition} -translate-y-full opacity-0`;
  }

  return `${transition} translate-y-full opacity-0 md:-translate-y-full md:opacity-0`;
};

/**
 * Returns width classes for toast content based on context.
 *
 * @param {Object} options
 * @param {boolean} [options.isDrawer]
 * @param {boolean|undefined} options.isMobile
 * @returns {string}
 */
export const getToastWidthClasses = ({ isDrawer, isMobile }) => {
  if (isDrawer || isMobile === true) {
    return "w-full";
  }

  if (isMobile === false) {
    return `w-full ${TOAST_DESKTOP_WIDTH_CLASS}`;
  }

  return `w-full md:min-w-[360px] md:max-w-[420px]`;
};

/**
 * Upgrades legacy bottom offsets and applies responsive desktop rules.
 *
 * @param {string} positionClasses
 * @param {boolean} isDrawer
 * @returns {string}
 */
export const applyDesktopTopPositionOverride = (positionClasses, isDrawer) => {
  if (isDrawer || !positionClasses || !positionClasses.includes("bottom")) {
    return positionClasses;
  }

  return `${positionClasses} md:bottom-auto ${DESKTOP_TOAST_TOP_CLASS}`;
};

/**
 * Normalizes legacy `position` classes to the global toast layout system.
 *
 * @param {string} positionClasses
 * @param {Object} options
 * @param {boolean} [options.isDrawer]
 * @param {boolean|undefined} options.isMobile
 * @returns {string}
 */
export const normalizeLegacyPositionClasses = (positionClasses, { isDrawer, isMobile }) => {
  if (!positionClasses) {
    return positionClasses;
  }

  let normalized = positionClasses
    .replace(/\bbottom-80\b/g, TOAST_MOBILE_BOTTOM_CLASS)
    .replace(/\bbottom-84\b/g, TOAST_MOBILE_BOTTOM_CLASS)
    .replace(/\bbottom-90\b/g, TOAST_MOBILE_BOTTOM_CLASS)
    .replace(/\bbottom-100\b/g, TOAST_MOBILE_BOTTOM_CLASS)
    .replace(/\bbottom-\[5rem\]/g, TOAST_MOBILE_BOTTOM_CLASS)
    .replace(/\bbottom-\[6rem\]/g, TOAST_MOBILE_BOTTOM_CLASS)
    .replace(/\bbottom-\[100px\]/g, TOAST_MOBILE_BOTTOM_CLASS)
    .replace(/\bmax-w-md\b/g, "")
    .replace(/\bmax-w-\[95%\]\b/g, "")
    .replace(/\bw-\[255px\]\b/g, "")
    .replace(/\bw-\[280px\]\b/g, "")
    .replace(/\bw-\[320px\]\b/g, "")
    .replace(/\bw-\[90%\]\b/g, "")
    .replace(/\bmd:w-fit\b/g, "")
    .replace(/\s+/g, " ")
    .trim();

  const hasCustomHorizontalPosition =
    /\bleft-\[/.test(normalized) || /\bleft-\d+\b/.test(normalized.replace(/\bleft-1\/2\b/g, ""));

  if (isDrawer) {
    normalized = normalized.replace(/\bfixed\b/g, "absolute");

    if (!hasCustomHorizontalPosition) {
      normalized = normalized
        .replace(/\bleft-1\/2\b/g, "")
        .replace(/\b-translate-x-1\/2\b/g, "")
        .replace(/\binset-x-20\b/g, "")
        .replace(/\bleft-20\b/g, "")
        .replace(/\bright-20\b/g, "");

      normalized = `${normalized} left-20 right-20`.replace(/\s+/g, " ").trim();
    }

    if (!normalized.includes("bottom-")) {
      normalized = `${normalized} ${TOAST_DRAWER_BOTTOM_CLASS}`.trim();
    }

    return normalized;
  }

  if (!hasCustomHorizontalPosition) {
    normalized = normalized
      .replace(/\bleft-1\/2\b/g, "")
      .replace(/\b-translate-x-1\/2\b/g, "")
      .replace(/\binset-x-20\b/g, "");

    if (isMobile === false) {
      normalized = `${normalized} left-1/2 -translate-x-1/2 ${TOAST_DESKTOP_WIDTH_CLASS}`.trim();
    } else {
      normalized = `${normalized} inset-x-20`.trim();
    }
  } else if (isMobile === false && normalized.includes("left-1/2")) {
    normalized = `${normalized} ${TOAST_DESKTOP_WIDTH_CLASS}`.trim();
  }

  if (!normalized.includes("bottom-") && !normalized.includes("top-")) {
    normalized = `${normalized} ${TOAST_MOBILE_BOTTOM_CLASS}`.trim();
  }

  normalized = applyDesktopTopPositionOverride(normalized, false);

  return applyToastZIndex(normalized.replace(/\s+/g, " ").trim());
};

/**
 * Splits legacy `position` prop into container vs content classes.
 *
 * @param {string} position
 * @returns {{ container: string, content: string, hasLayoutClasses: boolean }}
 */
export const splitToastPositionClasses = (position) => {
  if (!position) {
    return { container: "", content: "w-full", hasLayoutClasses: false };
  }

  const hasLayoutClasses = /\b(fixed|absolute|inset-x|left-|right-|top-|bottom-|translate-x)\b/.test(position);

  if (!hasLayoutClasses) {
    return { container: "", content: position || "w-full", hasLayoutClasses: false };
  }

  if (!position.includes("fixed") && !position.includes("absolute")) {
    return { container: position, content: "w-full", hasLayoutClasses: true };
  }

  return { container: position, content: "w-full", hasLayoutClasses: true };
};

/**
 * Default container positioning when Toast manages its own layout.
 *
 * @param {Object} options
 * @param {boolean} [options.isDrawer]
 * @param {boolean|undefined} options.isMobile
 * @param {"top"|"bottom"|"auto"} options.resolvedPlacement
 * @returns {string}
 */
export const getDefaultToastContainerClasses = ({ isDrawer, isMobile, resolvedPlacement }) => {
  if (isDrawer) {
    const vertical = resolvedPlacement === "top" ? "top-20" : TOAST_DRAWER_BOTTOM_CLASS;
    return applyToastZIndex(`absolute left-20 right-20 ${vertical}`);
  }

  const base = "fixed";

  if (isMobile === true) {
    return applyToastZIndex(`${base} ${TOAST_FULL_WIDTH_GAPS_CLASS} ${TOAST_MOBILE_BOTTOM_CLASS}`);
  }

  if (isMobile === false) {
    const vertical = resolvedPlacement === "bottom" ? DESKTOP_TOAST_BOTTOM_CLASS : "top-140";
    return applyToastZIndex(`${base} left-1/2 -translate-x-1/2 w-full ${TOAST_DESKTOP_WIDTH_CLASS} ${vertical}`);
  }

  return applyToastZIndex(
    `${base} ${TOAST_FULL_WIDTH_GAPS_CLASS} ${TOAST_MOBILE_BOTTOM_CLASS} md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:min-w-[360px] md:max-w-[420px] md:bottom-auto ${DESKTOP_TOAST_TOP_CLASS}`,
  );
};

/**
 * Resolves the outer toast container classes for all contexts.
 *
 * @param {Object} options
 * @param {boolean} [options.isDrawer]
 * @param {boolean|undefined} options.isMobile
 * @param {"top"|"bottom"|"auto"} options.resolvedPlacement
 * @param {string} [options.position]
 * @param {boolean} [options.isInline]
 * @returns {string}
 */
export const getToastContainerClasses = ({
  isDrawer,
  isMobile,
  resolvedPlacement,
  position = "",
  isInline = false,
}) => {
  if (isInline) {
    return "";
  }

  const { container, hasLayoutClasses } = splitToastPositionClasses(position);

  if (hasLayoutClasses && container) {
    return applyToastZIndex(normalizeLegacyPositionClasses(container, { isDrawer, isMobile }));
  }

  return getDefaultToastContainerClasses({ isDrawer, isMobile, resolvedPlacement });
};
